// client/src/hooks/support/useLogins.ts
//
// React Query hooks for the Support → Logins shared credential vault.
// All access goes through callables; the collection is server-only in Firestore.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  loginsKeys,
  type CreateLoginPayload,
  type SupportLogin,
  type UpdateLoginFields,
} from '../../types/support/logins';

function sortLogins(logins: SupportLogin[]): SupportLogin[] {
  return [...logins].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Nobody opens the Logins page often enough to keep `support_listLogins` warm,
 * so nearly every visit pays a Cloud Functions cold start — several seconds
 * before a single row can render. The page code cannot make the boot faster; it
 * can only start it earlier and then not throw the result away.
 *
 * 30 minutes because this is a credential vault a handful of people edit a few
 * times a month, not a live queue. The global default is 5 minutes, which meant
 * a second visit after lunch paid the cold start again.
 */
const LOGINS_STALE_TIME = 1000 * 60 * 30;

function fetchLogins(): Promise<SupportLogin[]> {
  const callable = httpsCallable<Record<string, never>, { logins: SupportLogin[] }>(
    functions,
    'support_listLogins',
  );
  // Server derives orgId from auth context — no payload needed
  return callable({}).then((res) => sortLogins(res.data.logins ?? []));
}

/** List all logins for the current org. */
export function useLogins() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';

  return useQuery({
    queryKey: loginsKeys.list(orgId),
    queryFn: fetchLogins,
    enabled: !!orgId,
    staleTime: LOGINS_STALE_TIME,
  });
}

/**
 * Start the fetch before the user has finished clicking.
 *
 * Called from `useNavPrefetch` on hover over the Logins nav link. The cold start
 * then overlaps the ~200ms+ of mouse travel and route transition instead of
 * beginning after it, and the query the page mounts with is the same one — same
 * key, same fetcher — so it reads the in-flight request rather than issuing a
 * second.
 */
export function prefetchLogins(queryClient: QueryClient, orgId: string): void {
  if (!orgId) return;
  void queryClient.prefetchQuery({
    queryKey: loginsKeys.list(orgId),
    queryFn: fetchLogins,
    staleTime: LOGINS_STALE_TIME,
  });
}

export function useCreateLogin() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const displayName = user?.displayName || user?.email || 'Unknown';
  const key = loginsKeys.list(orgId);

  return useMutation({
    mutationFn: async (payload: CreateLoginPayload) => {
      const callable = httpsCallable(functions, 'support_createLogin');
      await callable({ organizationId: orgId, ...payload });
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SupportLogin[]>(key);
      const now = Date.now();
      const placeholder: SupportLogin = {
        loginId: `__optimistic__${now}`,
        organizationId: orgId,
        label: payload.label,
        category: payload.category,
        url: payload.url ?? '',
        username: payload.username ?? '',
        notes: payload.notes ?? '',
        hasPassword: !!payload.password,
        createdAt: now,
        createdByName: displayName,
        updatedAt: now,
        updatedByName: displayName,
      };
      queryClient.setQueryData<SupportLogin[]>(key, sortLogins([...(previous ?? []), placeholder]));
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
      toast.error('Failed to add login');
    },
    onSuccess: () => {
      toast.success('Login added');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateLogin() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const key = loginsKeys.list(orgId);

  return useMutation({
    mutationFn: async ({ loginId, updates }: { loginId: string; updates: UpdateLoginFields }) => {
      const callable = httpsCallable(functions, 'support_updateLogin');
      await callable({ organizationId: orgId, loginId, updates });
    },
    onMutate: async ({ loginId, updates }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SupportLogin[]>(key);
      if (previous) {
        // Optimistically patch metadata (password is never in the cache).
        const { password: _password, ...meta } = updates;
        queryClient.setQueryData<SupportLogin[]>(
          key,
          previous.map((l) =>
            l.loginId === loginId
              ? {
                  ...l,
                  ...meta,
                  hasPassword:
                    typeof updates.password === 'string' && updates.password.length > 0
                      ? true
                      : l.hasPassword,
                }
              : l,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
      toast.error('Failed to save login');
    },
    onSuccess: () => {
      toast.success('Login updated');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteLogin() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const key = loginsKeys.list(orgId);

  return useMutation({
    mutationFn: async (loginId: string) => {
      const callable = httpsCallable(functions, 'support_deleteLogin');
      await callable({ organizationId: orgId, loginId });
    },
    onMutate: async (loginId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SupportLogin[]>(key);
      if (previous) {
        queryClient.setQueryData<SupportLogin[]>(
          key,
          previous.filter((l) => l.loginId !== loginId),
        );
      }
      return { previous };
    },
    onError: (_err, _loginId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
      toast.error('Failed to delete login');
    },
    onSuccess: () => {
      toast.success('Login deleted');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Reveal a single password on demand. Not cached — the plaintext is returned to
 * the caller and held only in local component state.
 */
export function useRevealLogin() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';

  return useMutation({
    mutationFn: async (loginId: string): Promise<string> => {
      const callable = httpsCallable<
        { organizationId: string; loginId: string },
        { password: string }
      >(functions, 'support_revealLogin');
      const res = await callable({ organizationId: orgId, loginId });
      return res.data.password ?? '';
    },
    onError: () => {
      toast.error('Failed to reveal password');
    },
  });
}
